import { FC } from 'react';
import { Box, Tooltip, SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { Help as HelpIcon } from '@mui/icons-material';

type SvgIconComponent = OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;

interface TreeNodeIconProps {
  icon?: SvgIconComponent;
  iconOverlay?: SvgIconComponent;
  iconOverlayTitle?: string;
  /** When true, the primary icon uses secondary text color (e.g. shipped samples). */
  iconMuted?: boolean;
}

/**
 * Renders a tree node icon, optionally with a small status badge (shipped, transferred, etc.).
 */
export const TreeNodeIcon: FC<TreeNodeIconProps> = ({
  icon: Icon = HelpIcon,
  iconOverlay: Overlay,
  iconOverlayTitle,
  iconMuted = false,
}) => {
  const iconColor = iconMuted ? 'text.secondary' : 'primary.main';

  if (!Overlay) {
    return <Icon sx={{ color: iconColor }} />;
  }

  const badgeIcon = (
    <Box
      component={iconOverlayTitle ? 'span' : 'div'}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: 22,
        height: 22,
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 20, color: iconColor }} />
      <Overlay
        sx={{
          position: 'absolute',
          bottom: -3,
          right: -5,
          fontSize: 12,
          color: 'text.secondary',
          bgcolor: 'background.paper',
          borderRadius: '50%',
        }}
      />
    </Box>
  );

  if (iconOverlayTitle) {
    return (
      <Tooltip title={iconOverlayTitle} placement="top">
        {badgeIcon}
      </Tooltip>
    );
  }

  return badgeIcon;
};
